<template>
  <div class="sales-guide">
    <div class="dash-page-header">
      <h2 class="dash-heading mb-0">Sales Guide</h2>
      <span class="sg-print-hint" @click="printGuide">
        <i class="ti ti-printer"></i> Print / Save PDF
      </span>
    </div>

    <div class="sg-doc" ref="doc">
      <p class="sg-intro">
        How to use this system to sell IPTV subscriptions, take payments, and get
        customers watching. No technical background needed — just follow the steps.
      </p>

      <div class="sg-warning">
        <strong><i class="ti ti-alert-triangle"></i> Read this first — automatic line setup is OFF</strong>
        The connection to the IPTV provider's panel is not working at the moment, so the
        system will <b>not</b> create customer logins automatically. After every sale you
        must <b>assign the login details to the customer yourself</b> (Step 4). Treat manual
        assignment as the normal job, not the exception, until the owner says the panel is back.
      </div>

      <!-- 1 -->
      <section>
        <h3><span class="sg-num">1</span> The two ways a customer can buy</h3>
        <p><b>A. Customer buys online (self-checkout).</b> They register, go to Pricing, pick a
          plan and pay by card. The payment is recorded and the subscription activates
          automatically — but you still assign their login (see the warning above).</p>
        <p><b>B. You sell to them directly (Bill Client).</b> You take the money yourself
          (cash, M-Pesa, transfer, WhatsApp deal) then activate their account from the
          admin using <b>Bill Client</b>. This is your main tool.</p>
        <div class="sg-note">
          <i class="ti ti-info-circle"></i>
          In both cases the customer must have a registered account first. The system can
          only activate a plan for a user who already exists. Selling to someone new? Have
          them register before you bill them.
        </div>
      </section>

      <!-- 2 -->
      <section>
        <h3><span class="sg-num">2</span> Find the customer &amp; Bill Client</h3>
        <ol>
          <li>Make sure the customer has an account (have them register if not).</li>
          <li>Go to the <b>Users</b> tab.</li>
          <li>Search by <b>name or email</b>.</li>
          <li>Click <b>Bill Client</b> next to their name.</li>
          <li>In the popup: choose the <b>package</b> they paid for; set a custom
            <b>amount</b> only if you agreed a discount; add a <b>note</b> (e.g.
            "Paid via M-Pesa ref XYZ").</li>
          <li>Confirm.</li>
        </ol>
        <p>The system then records a <b>completed payment</b>, <b>activates the subscription</b>
          with an expiry date, and creates the customer's line as <b>Pending assignment</b>.</p>
        <div class="sg-warning sg-warning-soft">
          <strong>You are not finished yet.</strong>
          The customer has paid and their account is active, but they have <b>no login</b>
          until you assign one. Go straight to Step 4. Don't tell the customer "check your
          email" until you've done that.
        </div>
      </section>

      <!-- 3 -->
      <section>
        <h3><span class="sg-num">3</span> Confirming an online payment manually</h3>
        <p>If a customer paid online but the payment shows <b>Pending</b>:</p>
        <ol>
          <li>Go to the <b>Payments</b> tab and find the payment (filter by Pending).</li>
          <li><b>Confirm the money actually arrived.</b></li>
          <li>Click <b>Complete</b> — this activates their subscription.</li>
          <li>Then assign their line (Step 4).</li>
        </ol>
        <p class="sg-muted">Only mark a payment complete after you've verified the funds.</p>
      </section>

      <!-- 4 -->
      <section>
        <h3><span class="sg-num">4</span> Give the customer their login — do this every sale</h3>
        <p>This is the step that actually delivers the product. Get a working IPTV login from
          the provider the way you normally do, then:</p>
        <ol>
          <li>Open the customer (from <b>Users</b>, or the <b>pending lines</b> list on Overview).</li>
          <li>Open their <b>line</b> and enter:
            <ul>
              <li><b>Server URL</b> — the provider's <b>Host</b> (e.g. <code>http://nxntv.top:80</code>)</li>
              <li><b>Username</b></li>
              <li><b>Password</b></li>
              <li><b>M3U URL</b> / <b>EPG URL</b> — only if you're using a playlist link</li>
            </ul>
          </li>
          <li>Set the line <b>Active</b> and save.</li>
        </ol>
        <p>The moment you save an <b>Active</b> line, the system <b>emails the login to the
          customer</b> automatically — that's their welcome email.</p>
        <div class="sg-note">
          <i class="ti ti-bulb"></i>
          <b>Xtream vs M3U:</b> Host + Username + Password is the "Login with Xtream Codes API"
          method — recommended, and faster for customers than a giant M3U playlist. Use
          <b>Email credentials</b> on a line to re-send a login someone lost.
        </div>
        <div class="sg-note sg-note-amber">
          <i class="ti ti-device-tv"></i>
          <b>MAG box customers:</b> get their <b>MAC address</b>, register it with the provider,
          then put the matching login on their line.
        </div>
      </section>

      <!-- 5 -->
      <section>
        <h3><span class="sg-num">5</span> Renewals</h3>
        <p>When an existing customer pays again, run <b>Bill Client</b> again — the system
          <b>extends</b> their subscription (no duplicate account). Renew their login with the
          provider, then make sure the line stays <b>Active</b> (re-send credentials if the
          login changed).</p>
      </section>

      <!-- 6 -->
      <section>
        <h3><span class="sg-num">6</span> Testing whether a line works</h3>
        <p>Test in an <b>IPTV app</b> (like IPTV Smarters), not a browser.</p>
        <ul>
          <li><b>Don't paste the URL into Chrome</b> — the M3U link tries to download the whole
            25,000+ channel list and looks frozen even when the line is perfect. The plain host
            shows a blank page. This is normal; the browser is not a media player.</li>
          <li>Quick "is it alive?" check: open
            <code>http://host:port/player_api.php?username=USER&password=PASS</code> — it returns
            a small JSON instantly if the line is active.</li>
        </ul>
      </section>

      <!-- 7 -->
      <section>
        <h3><span class="sg-num">7</span> About credits</h3>
        <p>Provider <b>credits</b> are spent only when you <b>create or renew a line</b> — never
          by watching. Once a line exists and is paid, viewing is unlimited until the expiry
          date. The only viewing limit is <b>Max Connections</b> (how many devices can stream at
          once). Watching a movie does <b>not</b> cost credits.</p>
      </section>

      <!-- 8 -->
      <section>
        <h3><span class="sg-num">8</span> If streams are slow</h3>
        <ul>
          <li><b>Menus/lists laggy</b> → it's the big M3U playlist. Use Xtream login instead.</li>
          <li><b>Video buffers during playback</b> → usually the <b>provider's servers</b>
            (especially at peak evening hours), your <b>internet bandwidth</b>, or <b>ISP
            throttling</b> (test with a VPN). Slow on every device and on mobile data too =
            it's the provider, not your setup.</li>
        </ul>
      </section>

      <!-- Checklist -->
      <section>
        <h3><span class="sg-num"><i class="ti ti-checklist"></i></span> Every-sale checklist</h3>
        <ul class="sg-check">
          <li>Confirm the money is actually received.</li>
          <li>Customer has a registered account.</li>
          <li><b>Bill Client</b> → correct package &amp; amount.</li>
          <li><b>Assign the line</b> with a working login and set it <b>Active</b>.</li>
          <li>Confirm the customer received the credentials email.</li>
          <li>End of day: <b>no customer left as "pending assignment".</b></li>
        </ul>
      </section>

      <div class="sg-rules">
        <h4>Golden rules</h4>
        <ol>
          <li>Money first, activate second — confirm payment before billing.</li>
          <li>The customer needs an account before you can sell to them.</li>
          <li>A sale isn't done until you've assigned the line.</li>
          <li>A "pending line" means a paid customer is still waiting — clear it the same day.</li>
        </ol>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";

export default defineComponent({
  name: "SalesGuide",
  setup() {
    const doc = ref<HTMLElement | null>(null);

    function printGuide() {
      const content = doc.value?.innerHTML ?? "";
      const win = window.open("", "_blank", "width=820,height=900");
      if (!win) return;
      win.document.write(`
        <html>
          <head>
            <title>GoPlayTV — Sales Guide</title>
            <style>
              body { font-family: Arial, sans-serif; color: #222; line-height: 1.6; max-width: 760px; margin: 24px auto; padding: 0 20px; }
              h1 { font-size: 22px; }
              h3 { color: #c90e0e; margin-top: 28px; }
              h4 { margin-bottom: 6px; }
              code { background: #f3f3f3; padding: 1px 5px; border-radius: 4px; font-size: 13px; }
              .sg-warning { background: #fff3cd; border: 1px solid #ffc107; padding: 12px 16px; border-radius: 8px; margin: 16px 0; }
              .sg-warning-soft { background: #e8f4fd; border-color: #bee3f8; }
              .sg-note { background: #f5f5f5; border-left: 3px solid #c90e0e; padding: 10px 14px; border-radius: 4px; margin: 12px 0; }
              .sg-note-amber { border-left-color: #ffc107; }
              .sg-rules { background: #1a0508; color: #fff; padding: 16px 20px; border-radius: 8px; margin-top: 24px; }
              .sg-num { display: inline-block; background: #c90e0e; color: #fff; width: 22px; height: 22px; border-radius: 50%; text-align: center; font-size: 13px; margin-right: 6px; }
              .sg-print-hint { display: none; }
              .sg-intro { font-size: 15px; }
            </style>
          </head>
          <body><h1>GoPlayTV — Sales Guide</h1>${content}</body>
        </html>`);
      win.document.close();
      win.focus();
      win.print();
    }

    return { doc, printGuide };
  },
});
</script>

<style scoped>
.sg-doc { max-width: 820px; }
.sg-intro { color: #c0c0c0; font-size: 0.95rem; line-height: 1.7; }
.sg-doc section { margin-top: 1.75rem; }
.sg-doc h3 {
  color: #fff; font-size: 1.05rem; font-weight: 700;
  display: flex; align-items: center; gap: 0.5rem; margin-bottom: 0.75rem;
}
.sg-num {
  display: inline-flex; align-items: center; justify-content: center;
  background: #c90e0e; color: #fff; width: 24px; height: 24px;
  border-radius: 50%; font-size: 0.8rem; font-weight: 800; flex-shrink: 0;
}
.sg-doc p { color: #c0c0c0; font-size: 0.9rem; line-height: 1.7; margin-bottom: 0.6rem; }
.sg-doc b { color: #fff; }
.sg-doc ol, .sg-doc ul { color: #c0c0c0; font-size: 0.9rem; line-height: 1.75; padding-left: 1.3rem; margin-bottom: 0.6rem; }
.sg-doc li { margin-bottom: 0.25rem; }
.sg-doc code {
  background: #2b1e1f; color: #ff9a9a; padding: 1px 6px;
  border-radius: 4px; font-size: 0.82rem; word-break: break-all;
}
.sg-muted, .sg-doc .sg-muted { color: #888; font-size: 0.82rem; }

.sg-warning {
  background: rgba(255, 193, 7, 0.1); border: 1px solid #ffc107;
  border-radius: 8px; padding: 0.9rem 1.1rem; margin: 1rem 0;
  color: #e8d9a0; font-size: 0.88rem; line-height: 1.6;
}
.sg-warning strong { display: block; color: #ffc107; margin-bottom: 0.4rem; }
.sg-warning b { color: #fff; }
.sg-warning-soft { background: rgba(96, 165, 250, 0.08); border-color: #60a5fa; color: #bcd6f5; }
.sg-warning-soft strong { color: #60a5fa; }

.sg-note {
  display: flex; gap: 0.6rem; align-items: flex-start;
  background: #201516; border-left: 3px solid #c90e0e;
  border-radius: 6px; padding: 0.7rem 0.9rem; margin: 0.85rem 0;
  color: #c0c0c0; font-size: 0.85rem; line-height: 1.6;
}
.sg-note i { color: #c90e0e; font-size: 1.05rem; margin-top: 2px; flex-shrink: 0; }
.sg-note b { color: #fff; }
.sg-note-amber { border-left-color: #ffc107; }
.sg-note-amber i { color: #ffc107; }

.sg-check li::marker { color: #4ade80; }
.sg-rules {
  background: linear-gradient(135deg, #1a0508, #2a0a0e);
  border: 1px solid #c90e0e; border-radius: 10px;
  padding: 1.1rem 1.4rem; margin-top: 2rem;
}
.sg-rules h4 { color: #fff; font-weight: 800; margin-bottom: 0.5rem; font-size: 1rem; }
.sg-rules ol { color: #e0c0c0; margin-bottom: 0; }

.sg-print-hint {
  display: inline-flex; align-items: center; gap: 0.4rem;
  color: #60a5fa; font-size: 0.85rem; cursor: pointer;
  padding: 0.4rem 0.8rem; border-radius: 6px; transition: background 0.2s;
}
.sg-print-hint:hover { background: #2b1e1f; }
</style>
